import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowDownRight, ArrowUpRight, Banknote, ChevronDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { api } from "../../lib/api";
import { fmtMoney } from "../ops/opsUtils";

const PERIODS = [
  { key: "week", label: "Week", prev: "same point last week" },
  { key: "month", label: "Month", prev: "same point last month" },
  { key: "quarter", label: "Quarter", prev: "same point last quarter" },
  { key: "ytd", label: "YTD", prev: "same point last year" },
  { key: "all", label: "All Time" },
];

const MORE = [
  { key: "prev_week", label: "Previous Week" },
  { key: "prev_month", label: "Previous Month" },
  { key: "prev_quarter", label: "Previous Quarter" },
  { key: "last_30", label: "Last 30 Days" },
  { key: "last_90", label: "Last 90 Days" },
  { key: "last_12m", label: "Last 12 Months" },
];

const fmtDay = (iso) => iso ? new Date(`${iso}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";

const Change = ({ cur, prevLabel }) => {
  if (!cur.previous) return null;
  const p = cur.change_pct;
  const up = p != null && p >= 0;
  return (
    <div className="text-xs text-[#6B7280] mt-1 flex items-center gap-1" data-testid="deployment-change">
      {p == null ? <span>No deployment {prevLabel || `in ${fmtDay(cur.previous.start)} – ${fmtDay(cur.previous.end)}`}</span> : (
        <>
          <span className={`inline-flex items-center font-medium ${up ? "text-[#047857]" : "text-[#B91C1C]"}`}>
            {up ? <ArrowUpRight className="w-3.5 h-3.5" /> : <ArrowDownRight className="w-3.5 h-3.5" />}{Math.abs(p * 100).toFixed(0)}%
          </span>
          <span>vs {fmtMoney(cur.previous.principal_deployed)} {prevLabel || `in ${fmtDay(cur.previous.start)} – ${fmtDay(cur.previous.end)}`}</span>
        </>
      )}
    </div>
  );
};

// Canonical Principal Deployed card. Pass `data` (dashboard payload) or let it fetch the shared endpoint.
export const DeploymentPeriodCard = ({ data: given, compact, defaultPeriod = "month" }) => {
  const [data, setData] = useState(given || null);
  const [period, setPeriod] = useState(defaultPeriod);
  const [extra, setExtra] = useState(null); // {key,label,start,end, data}
  const [moreOpen, setMoreOpen] = useState(false);
  const [custom, setCustom] = useState({ start: "", end: "" });
  const pickExtra = async (key, label, start, end) => {
    try {
      const { data: d } = await api.get("/operations/metrics/deployment/range", { params: { preset: key, start, end } });
      setExtra({ key, label, data: d.range });
      setMoreOpen(false);
    } catch (e) { /* keep current selection */ }
  };
  useEffect(() => {
    if (given) { setData(given); return; }
    api.get("/operations/metrics/deployment").then(({ data: d }) => setData(d)).catch(() => setData(null));
  }, [given]);
  if (!data) return null;
  const meta = extra ? { prev: null } : PERIODS.find((p) => p.key === period);
  const cur = extra ? extra.data : data.periods[period];

  return (
    <div className="bg-white border border-[#0066FF]/25 rounded-lg p-4 bg-gradient-to-br from-white to-[#F0F6FF]" data-testid="deployment-card">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-[#6B7280]">
          <Banknote className="w-3.5 h-3.5" /> Principal deployed
        </div>
        <div className="inline-flex rounded-full border border-[#E5E7EB] bg-white p-0.5" role="tablist" data-testid="deployment-period-selector">
          {PERIODS.map((p) => (
            <button key={p.key} onClick={() => { setExtra(null); setPeriod(p.key); }} role="tab" aria-selected={!extra && period === p.key}
              className={`px-2.5 py-1 text-[11px] rounded-full transition-colors ${!extra && period === p.key ? "bg-[#0A2540] text-white" : "text-[#374151] hover:text-[#0A2540]"}`}
              data-testid={`deployment-period-${p.key}`}>{p.label}</button>
          ))}
          <Popover open={moreOpen} onOpenChange={setMoreOpen}>
            <PopoverTrigger asChild>
              <button className={`px-2.5 py-1 text-[11px] rounded-full inline-flex items-center gap-0.5 transition-colors ${extra ? "bg-[#0A2540] text-white" : "text-[#374151] hover:text-[#0A2540]"}`}
                data-testid="deployment-period-more">{extra ? extra.label : "More"}<ChevronDown className="w-3 h-3" /></button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-64 p-2" data-testid="deployment-more-menu">
              {MORE.map((m) => (
                <button key={m.key} onClick={() => pickExtra(m.key, m.label)} data-testid={`deployment-more-${m.key}`}
                  className={`w-full text-left text-sm px-2.5 py-1.5 rounded-md hover:bg-[#F3F4F6] ${extra?.key === m.key ? "font-semibold text-[#0A2540]" : "text-[#374151]"}`}>{m.label}</button>
              ))}
              <div className="border-t border-[#E5E7EB] mt-1 pt-2 px-1">
                <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-1">Custom date range (ET)</div>
                <div className="flex items-center gap-1">
                  <input type="date" value={custom.start} onChange={(e) => setCustom((c) => ({ ...c, start: e.target.value }))}
                    className="h-8 flex-1 min-w-0 rounded border border-[#D1D5DB] px-1 text-xs" data-testid="deployment-custom-start" />
                  <input type="date" value={custom.end} onChange={(e) => setCustom((c) => ({ ...c, end: e.target.value }))}
                    className="h-8 flex-1 min-w-0 rounded border border-[#D1D5DB] px-1 text-xs" data-testid="deployment-custom-end" />
                </div>
                <button disabled={!custom.start || !custom.end || custom.start > custom.end}
                  onClick={() => pickExtra("custom", `${fmtDay(custom.start)} – ${fmtDay(custom.end)}`, custom.start, custom.end)}
                  className="mt-2 w-full h-8 rounded-md bg-[#0A2540] text-white text-xs disabled:opacity-40" data-testid="deployment-custom-apply">Apply range</button>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>
      <div className={`grid ${compact ? "grid-cols-3" : "grid-cols-1 sm:grid-cols-3"} gap-4`}>
        <div>
          <div className="font-display text-2xl text-[#0A2540] tabular-nums leading-tight" data-testid="deployment-principal">{fmtMoney(cur.principal_deployed)}</div>
          {!compact && <Change cur={cur} prevLabel={meta.prev} />}
        </div>
        <div>
          <div className="font-display text-2xl text-[#0A2540] tabular-nums leading-tight" data-testid="deployment-advances">{cur.advances_funded}</div>
          <div className="text-xs text-[#6B7280]">advances funded{cur.additional_advances ? ` · incl. ${cur.additional_advances} additional` : ""}</div>
        </div>
        <div>
          <div className="font-display text-2xl text-[#0A2540] tabular-nums leading-tight" data-testid="deployment-average">{cur.average_advance != null ? fmtMoney(cur.average_advance) : "—"}</div>
          <div className="text-xs text-[#6B7280]">average advance</div>
        </div>
      </div>
      <div className="text-[11px] text-[#9CA3AF] mt-3" data-testid="deployment-range">
        {cur.start ? `${fmtDay(cur.start)} – ${fmtDay(cur.end)}` : `All time through ${fmtDay(cur.end)}`} · Eastern Time · actual funded advances (original + additional), excluding declined, deleted, test and demo loans
        {compact && <> · <Link to="/admin" className="text-[#0066FF] hover:underline" data-testid="deployment-dashboard-link">Executive dashboard</Link></>}
      </div>
    </div>
  );
};
